import React, { useMemo } from "react";
import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  TextField,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

interface Audience {
  title: string;
  id?: string;
  type: "CUSTOM_AUDIENCE" | "MESSENGER_OPTIN";
}

const AUDIENCE_LABELS = {
  CUSTOM_AUDIENCE: "Custom Audiences",
  MESSENGER_OPTIN: "Messenger Opt-ins",
} as const;

const ALL_OPTIN_TITLE = "All";

const styles = {
  groupHeader: {
    fontWeight: 600,
    fontSize: "0.9rem",
    backgroundColor: "#f5f5f5",
    padding: "4px 12px",
  },
  listContainer: {
    margin: 0,
    padding: 0,
  },
  listItem: {
    padding: "0px",
  },
  checkbox: {
    marginRight: 8,
  },
} as const;

interface AudienceSelectionProps {
  audiences: Audience[];
  selectedAudiences: Audience[];
  loading: boolean;
  onOpen: () => void;
  onClose: () => void;
  onChange: (newValue: Audience[]) => void;
  openDropdown: boolean;
}

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AudienceSelector: React.FC<AudienceSelectionProps> = ({
  audiences,
  selectedAudiences,
  loading,
  openDropdown,
  onOpen,
  onClose,
  onChange,
}) => {
  const isAllOptinSelected = useMemo(
    () =>
      selectedAudiences.some(
        (a) => a.title === ALL_OPTIN_TITLE && a.type === "MESSENGER_OPTIN"
      ),
    [selectedAudiences]
  );

  const isOptionDisabled = (option: Audience): boolean => {
    return (
      option.type === "MESSENGER_OPTIN" &&
      option.title !== ALL_OPTIN_TITLE &&
      isAllOptinSelected
    );
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={audiences}
      value={selectedAudiences}
      open={openDropdown}
      onOpen={onOpen}
      onClose={onClose}
      onChange={(_, newValue) => onChange(newValue)}
      isOptionEqualToValue={(option, value) => {
        if (option.type === "CUSTOM_AUDIENCE") return option.id === value.id;
        return option.title === value.title && option.type === value.type;
      }}
      getOptionLabel={(option) => option.title || ""}
      groupBy={(option) => option.type}
      renderGroup={(params) => (
        <li key={params.key}>
          <div style={styles.groupHeader}>
            {AUDIENCE_LABELS[params.group as keyof typeof AUDIENCE_LABELS]}
          </div>
          <ul style={styles.listContainer}>{params.children}</ul>
        </li>
      )}
      getOptionDisabled={isOptionDisabled}
      renderOption={(props, option, { selected }) => (
        <li {...props} style={styles.listItem}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            checked={selected}
            style={styles.checkbox}
          />
          {option.title}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Audiences"
          placeholder="Select audiences..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{ mt: 3 }}
    />
  );
};

export default AudienceSelector;
